import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import {
  UserDashboard,
  DashboardWidget,
  ChartSchema,
  WidgetDataResponse,
  CreateDashboardRequest,
  UpdateDashboardRequest,
  CreateWidgetRequest,
  UpdateWidgetRequest,
  PaginatedResponse,
  DashboardListFilters,
  ExportOptions
} from '../../types/dashboard.types';

export interface DashboardListParams extends DashboardListFilters {
  page?: number;
  limit?: number;
}

export interface WidgetListParams {
  dashboardId: string;
}

export interface WidgetDataParams {
  dashboardId: string;
  widgetId: string;
}

export interface CreateWidgetParams {
  dashboardId: string;
  widget: CreateWidgetRequest;
}

export interface UpdateWidgetParams {
  dashboardId: string;
  widgetId: string;
  widget: UpdateWidgetRequest;
}

export interface DeleteWidgetParams {
  dashboardId: string;
  widgetId: string;
}

export interface ExportDashboardParams {
  dashboardId: string;
  options: ExportOptions;
}

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['Dashboard', 'Widget', 'ChartSchema', 'WidgetData'],
  keepUnusedDataFor: 300, // Keep dashboard data cached for 5 minutes
  endpoints: (builder) => ({
    // Chart Schema
    getChartSchema: builder.query<ChartSchema, void>({
      query: () => ({
        url: 'dashboard/chart-schema/',
        method: 'GET',
      }),
      providesTags: ['ChartSchema'],
    }),

    // Dashboard CRUD
    getDashboards: builder.query<PaginatedResponse<UserDashboard>, DashboardListParams>({
      query: (params = {}) => {
        const { page = 1, limit = 20, search, sortBy, sortDirection } = params;
        const queryParams: Record<string, string | number> = {
          page: page.toString(),
          limit: limit.toString(),
        };

        if (search && typeof search === 'string') {
          queryParams.search = search;
        }
        if (sortBy && typeof sortBy === 'string') {
          const ordering = sortDirection === 'desc' ? `-${sortBy}` : sortBy;
          queryParams.ordering = ordering;
        }

        return {
          url: 'dashboard/dashboards/',
          method: 'GET',
          params: queryParams,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'Dashboard' as const, id })),
              { type: 'Dashboard', id: 'LIST' },
            ]
          : [{ type: 'Dashboard', id: 'LIST' }],
    }),

    getDashboard: builder.query<UserDashboard, string>({
      query: (dashboardId) => ({
        url: `dashboard/dashboards/${dashboardId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, dashboardId) => [
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    createDashboard: builder.mutation<UserDashboard, CreateDashboardRequest>({
      query: (dashboard) => ({
        url: 'dashboard/dashboards/',
        method: 'POST',
        body: dashboard,
      }),
      invalidatesTags: [{ type: 'Dashboard', id: 'LIST' }],
    }),

    updateDashboard: builder.mutation<UserDashboard, { id: string; dashboard: UpdateDashboardRequest }>({
      query: ({ id, dashboard }) => ({
        url: `dashboard/dashboards/${id}/`,
        method: 'PATCH',
        body: dashboard,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Dashboard', id },
        { type: 'Dashboard', id: 'LIST' },
      ],
    }),

    deleteDashboard: builder.mutation<void, string>({
      query: (dashboardId) => ({
        url: `dashboard/dashboards/${dashboardId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, dashboardId) => [
        { type: 'Dashboard', id: dashboardId },
        { type: 'Dashboard', id: 'LIST' },
        { type: 'Widget', id: `LIST_${dashboardId}` },
      ],
    }),

    // Widget CRUD
    getWidgets: builder.query<DashboardWidget[], WidgetListParams>({
      query: ({ dashboardId }) => ({
        url: `dashboard/dashboards/${dashboardId}/widgets/`,
        method: 'GET',
      }),
      providesTags: (result, error, { dashboardId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Widget' as const, id })),
              { type: 'Widget', id: `LIST_${dashboardId}` },
              { type: 'Dashboard', id: dashboardId },
            ]
          : [{ type: 'Widget', id: `LIST_${dashboardId}` }],
    }),

    getWidget: builder.query<DashboardWidget, { dashboardId: string; widgetId: string }>({
      query: ({ dashboardId, widgetId }) => ({
        url: `dashboard/dashboards/${dashboardId}/widgets/${widgetId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { widgetId }) => [
        { type: 'Widget', id: widgetId },
      ],
    }),

    createWidget: builder.mutation<DashboardWidget, CreateWidgetParams>({
      query: ({ dashboardId, widget }) => ({
        url: `dashboard/dashboards/${dashboardId}/widgets/`,
        method: 'POST',
        body: {
          ...widget,
          dashboard: dashboardId,
        },
      }),
      invalidatesTags: (result, error, { dashboardId }) => [
        { type: 'Widget', id: `LIST_${dashboardId}` },
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    updateWidget: builder.mutation<DashboardWidget, UpdateWidgetParams>({
      query: ({ dashboardId, widgetId, widget }) => ({
        url: `dashboard/dashboards/${dashboardId}/widgets/${widgetId}/`,
        method: 'PATCH',
        body: widget,
      }),
      invalidatesTags: (result, error, { dashboardId, widgetId }) => [
        { type: 'Widget', id: widgetId },
        { type: 'Widget', id: `LIST_${dashboardId}` },
        { type: 'Dashboard', id: dashboardId },
        { type: 'WidgetData', id: widgetId },
      ],
    }),

    deleteWidget: builder.mutation<void, DeleteWidgetParams>({
      query: ({ dashboardId, widgetId }) => ({
        url: `dashboard/dashboards/${dashboardId}/widgets/${widgetId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { dashboardId, widgetId }) => [
        { type: 'Widget', id: widgetId },
        { type: 'Widget', id: `LIST_${dashboardId}` },
        { type: 'Dashboard', id: dashboardId },
        { type: 'WidgetData', id: widgetId },
      ],
    }),

    // Widget Data
    getWidgetData: builder.query<WidgetDataResponse, WidgetDataParams>({
      query: ({ dashboardId, widgetId }) => ({
        url: `dashboard/dashboards/${dashboardId}/widgets/${widgetId}/data/`,
        method: 'GET',
      }),
      providesTags: (result, error, { widgetId }) => [
        { type: 'WidgetData', id: widgetId },
      ],
      // Refetch widget data every 5 minutes for real-time updates
      keepUnusedDataFor: 300,
    }),

    // Bulk operations
    updateWidgetPositions: builder.mutation<void, { dashboardId: string; widgets: Array<{ id: string; position: number }> }>({
      queryFn: async ({ dashboardId, widgets }, api, extraOptions, baseQuery) => {
        // Update multiple widget positions in parallel
        const promises = widgets.map(({ id, position }) =>
          baseQuery({
            url: `dashboard/dashboards/${dashboardId}/widgets/${id}/`,
            method: 'PATCH',
            body: { position },
          })
        );

        try {
          await Promise.all(promises);
          return { data: undefined as void };
        } catch (error: any) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: error.message || 'Failed to update widget positions',
              data: error
            }
          };
        }
      },
      invalidatesTags: (result, error, { dashboardId }) => [
        { type: 'Widget', id: `LIST_${dashboardId}` },
        { type: 'Dashboard', id: dashboardId },
      ],
    }),

    // Export functionality
    exportDashboard: builder.mutation<Blob, ExportDashboardParams>({
      query: ({ dashboardId, options }) => ({
        url: `dashboard/dashboards/${dashboardId}/export/`,
        method: 'POST',
        body: options,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    exportWidget: builder.mutation<Blob, { dashboardId: string; widgetId: string; format: string }>({
      query: ({ dashboardId, widgetId, format }) => ({
        url: `dashboard/dashboards/${dashboardId}/widgets/${widgetId}/export/`,
        method: 'POST',
        body: { format },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
  }),
});

// Export hooks
export const {
  // Chart Schema
  useGetChartSchemaQuery,

  // Dashboard CRUD
  useGetDashboardsQuery,
  useGetDashboardQuery,
  useCreateDashboardMutation,
  useUpdateDashboardMutation,
  useDeleteDashboardMutation,

  // Widget CRUD
  useGetWidgetsQuery,
  useGetWidgetQuery,
  useCreateWidgetMutation,
  useUpdateWidgetMutation,
  useDeleteWidgetMutation,

  // Widget Data
  useGetWidgetDataQuery,

  // Bulk operations
  useUpdateWidgetPositionsMutation,

  // Export
  useExportDashboardMutation,
  useExportWidgetMutation,

  // Lazy queries for manual triggering
  useLazyGetWidgetDataQuery,
  useLazyGetChartSchemaQuery,
} = dashboardApi;

// Utility functions for cache management
export const dashboardApiUtils = {
  // Invalidate all dashboard data
  invalidateAllDashboards: (dispatch: any) => {
    dispatch(dashboardApi.util.invalidateTags(['Dashboard', 'Widget', 'WidgetData']));
  },

  // Invalidate specific dashboard
  invalidateDashboard: (dispatch: any, dashboardId: string) => {
    dispatch(dashboardApi.util.invalidateTags([
      { type: 'Dashboard', id: dashboardId },
      { type: 'Widget', id: `LIST_${dashboardId}` },
    ]));
  },

  // Invalidate widget data for real-time updates
  invalidateWidgetData: (dispatch: any, widgetId: string) => {
    dispatch(dashboardApi.util.invalidateTags([
      { type: 'WidgetData', id: widgetId },
    ]));
  },

  // Prefetch dashboard data
  prefetchDashboard: (dispatch: any, dashboardId: string) => {
    dispatch(dashboardApi.util.prefetch('getDashboard', dashboardId, { force: false }));
    dispatch(dashboardApi.util.prefetch('getWidgets', { dashboardId }, { force: false }));
  },
};